import type { ReactNode } from "react";
import { ScanText, Sigma, Sparkles, TestTube2 } from "lucide-react";
import type { ColumnType } from "../models/audit/auditSession";

/**
 * The single source for how a column kind is drawn — its label, its glyph and
 * its chip colour, for the surfaces that read it; a second definition elsewhere
 * forks the value the first time the two disagree.
 *
 * `WorkPaperTemplate.tsx` still carries its own forks — three icon branches and
 * one label-only branch — and they already disagree on label, chip and colour.
 * They cannot adopt this map as it stands: `icon` is a pre-rendered node with a
 * frozen `className`, and each of those sites needs a different one (inline,
 * a right margin, a shrink guard), which a finished node cannot express.
 *
 * Keyed on `ColumnType` rather than `string` so a new column kind cannot be
 * added to the union without giving it an entry.
 *
 * `bgColor` is optional because the analytics kind has no chip colour assigned.
 * The three coloured kinds inherit no palette keyed on kind, so there is nothing
 * to derive one from and the choice stays open. Its glyph renders bare and takes
 * the surrounding text colour; borrowing another kind's chip would read as that
 * kind.
 */
export const columnTypeConfig: Record<
  ColumnType,
  { label: string; icon: ReactNode; bgColor?: string }
> = {
  support: {
    label: "Support",
    icon: <ScanText className="h-4 w-4 text-amber-700" />,
    bgColor: "bg-amber-100",
  },
  evaluated: {
    label: "Evaluated",
    icon: <Sparkles className="h-4 w-4 text-blue-600" />,
    bgColor: "bg-blue-100",
  },
  attribute_test: {
    label: "Attribute Test",
    icon: <TestTube2 className="h-4 w-4 text-purple-600" />,
    bgColor: "bg-purple-100",
  },
  analytics: {
    label: "Analytics",
    icon: <Sigma className="h-4 w-4" />,
  },
};
